import { useCallback, useMemo, useState } from 'react'
import { Head, router } from '@inertiajs/react'
import { MailOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import EventLayout from '@/components/layouts/EventLayout'
import TabbedPage from '@/components/shared/TabbedPage'
import { TabIconLabel } from '@/components/shared/TabIconLabel'
import CertificateDesignTab from '@/components/certificates/CertificateDesignTab'
import CertificateEmailTab from '@/components/certificates/CertificateEmailTab'
import { automatedEmailIndexPath } from '@/pages/events/messaging/email_blasts/components/AutomatedEmailForm'
import type {
  AutomatedEmail,
  Event as EmailBlastEvent,
} from '@/pages/events/messaging/email_blasts/types'
import type { MergeTagOptions } from '@/lib/mergeTags'
import type {
  CertificateEventData,
  CertificateMergeTag,
  CertificatePreviewRegistrant,
  CertificateTemplate,
} from '@/types/certificateDesigner'

export type CertificatesPageProps = {
  eventSlug: string
  eventData: CertificateEventData
  certificateTemplate?: CertificateTemplate
  /** The designer canvas vocabulary — bare keys, not `%%...%%` tags. */
  mergeTags?: CertificateMergeTag[]
  /** The Unlayer editor vocabulary, for the Email Content tab. */
  mergeTagOptions?: MergeTagOptions
  /** The opening slice of the preview picker, not the whole event. */
  registrantsForPreview?: CertificatePreviewRegistrant[]
  /** How many registrants the event has, so the picker can admit what it hides. */
  registrantCount?: number
  automatedEmail: AutomatedEmail
}

const CERTIFICATE_DESIGNER_TAB = 'certificate-designer'
const EMAIL_CONTENT_TAB = 'email-content'

/**
 * Tab keys are part of the server contract, not just local state:
 * `EmailBlastsController#automated_email_return_path` redirects back here with
 * `tab=email-content` after the Email Content form saves.
 */
const CERTIFICATE_TAB_KEYS = [CERTIFICATE_DESIGNER_TAB, EMAIL_CONTENT_TAB]

const getTabFromUrl = () => {
  if (typeof window === 'undefined') return CERTIFICATE_DESIGNER_TAB

  const tab = new URLSearchParams(window.location.search).get('tab')
  return tab && CERTIFICATE_TAB_KEYS.includes(tab) ? tab : CERTIFICATE_DESIGNER_TAB
}

/**
 * The e-certificate builder.
 *
 * Reached from the E-Certificate row's Edit action on the Email Blast page —
 * there is deliberately no sidebar entry, because an e-certificate is an
 * automated email that happens to carry a designed attachment, not a feature of
 * its own. `EmailBlastsController#edit_automated` redirects here too, so old
 * deep links to the generic automated email editor still land in the right
 * place.
 */
export default function CertificatesPage({
  eventSlug,
  eventData,
  certificateTemplate,
  mergeTags = [],
  mergeTagOptions,
  registrantsForPreview = [],
  registrantCount = 0,
  automatedEmail,
}: CertificatesPageProps) {
  const [activeTabKey, setActiveTabKey] = useState(getTabFromUrl)

  const handleTabChange = useCallback((key: string) => {
    setActiveTabKey(key)

    if (typeof window === 'undefined') return

    // replaceState rather than an Inertia visit: both tabs are already mounted
    // with their props, so a round trip would only discard unsaved work. The URL
    // is updated so a refresh — or the redirect the email form triggers — comes
    // back to the tab the user was on.
    const url = new URL(window.location.href)
    url.searchParams.set('tab', key)
    window.history.replaceState({}, '', url.toString())
  }, [])

  /**
   * Where both the header's back arrow and the Email Content tab's Cancel go.
   * One function, so the two can never disagree.
   */
  const handleBack = useCallback(() => {
    router.visit(automatedEmailIndexPath(eventSlug))
  }, [eventSlug])

  /**
   * `AutomatedEmailForm` wants the Email Blast page's own `Event` shape, which
   * is narrower than the designer's `eventData` and insists on the two dates.
   * Built explicitly rather than cast: the form only reads `slug`, but leaving
   * the other fields undefined would be a lie the type system could not catch.
   */
  const emailEvent = useMemo<EmailBlastEvent>(
    () => ({
      id: eventData?.id ?? 0,
      name: eventData?.name ?? '',
      slug: eventData?.slug ?? eventSlug,
      start_date: eventData?.start_date ?? '',
      end_date: eventData?.end_date ?? '',
    }),
    [eventData, eventSlug]
  )

  const tabs = useMemo(
    () => [
      {
        key: CERTIFICATE_DESIGNER_TAB,
        label: <TabIconLabel icon={<SafetyCertificateOutlined />} label="Certificate Designer" />,
        children: (
          <CertificateDesignTab
            eventSlug={eventSlug}
            eventData={eventData}
            certificateTemplate={certificateTemplate}
            mergeTags={mergeTags}
            registrantsForPreview={registrantsForPreview}
            registrantCount={registrantCount}
          />
        ),
      },
      {
        key: EMAIL_CONTENT_TAB,
        label: <TabIconLabel icon={<MailOutlined />} label="Email Content" />,
        children: (
          <CertificateEmailTab
            event={emailEvent}
            automatedEmail={automatedEmail}
            mergeTagOptions={mergeTagOptions}
            onCancel={handleBack}
          />
        ),
      },
    ],
    [
      eventSlug,
      eventData,
      certificateTemplate,
      mergeTags,
      registrantsForPreview,
      registrantCount,
      emailEvent,
      automatedEmail,
      mergeTagOptions,
      handleBack,
    ]
  )

  return (
    <EventLayout>
      <Head title={`${eventData?.name || 'Event'} - E-Certificate`} />
      <TabbedPage
        title="E-Certificate"
        subtitle="Design the certificate registrants receive, and the email that delivers it."
        breadcrumb={[
          { title: 'Email Blast', href: automatedEmailIndexPath(eventSlug) },
          { title: 'E-Certificate' },
        ]}
        showBackButton
        onBack={handleBack}
        tabs={tabs}
        activeKey={activeTabKey}
        onTabChange={handleTabChange}
      />
    </EventLayout>
  )
}
